import { lazy, Suspense, useContext, useEffect, useState } from "react";
import Saidbar from "./component/Saidbar";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import MaintenancePage from "./MaintenancePage.jsx";

const IntegrationAdd = lazy(() => import("./page/Integration_add"));
const IntegrationList = lazy(() => import("./page/Integration_list"));
const IntegrationDashboard = lazy(() => import("./page/Integration_dashboard"));
const ProductsCatalog = lazy(() => import("./page/ProductsCatalog"));
const OrderTracking = lazy(() => import("./page/OrderTracking"));
const LivraisonAdd = lazy(() => import("./page/Livraison/Livraison_add"));
const LivraisonList = lazy(() => import("./page/Livraison/Livraison_list"));
const SubscriptionDashboard = lazy(() => import("./page/subscriptions/SubscriptionDashboard"));
const SubscriptionList = lazy(() => import("./page/subscriptions/SubscriptionList"));
const TeamUsersList = lazy(() => import("./page/User/TeamUsersList.jsx"));
const TeamAddUser = lazy(() => import("./page/User/TeamAddUser.jsx"));
const ClientsDashboard = lazy(() => import("./page/Clients/pages/clients/ClientsDashboard.jsx"));
const ClientsList = lazy(() => import("./page/Clients/pages/clients/ClientsList.jsx"));
const ClientAdd = lazy(() => import("./page/Clients/pages/clients/ClientAdd.jsx"));
const ClientScoring = lazy(() => import("./page/Clients/pages/clients/ClientScoring.jsx"));


const OrdersDashboard = lazy(() => import("../commerçant/page/orders/pages/orders/OrdersDashboard.jsx"));
const PendingOrders = lazy(() => import("../commerçant/page/orders/pages/orders/PendingOrders"));
const DeliveryDispatch = lazy(() => import("../commerçant/page/orders/pages/orders/DeliveryDispatch"));
const AddOrders = lazy(() => import("./page/orders/pages/orders/AddOrders.jsx"));
const IntegrationState = lazy(() => import("./page/IntegrationState.jsx"));
const DeliveryConnect = lazy(() => import("./page/Livraison/DeliveryConnect.jsx"));
const Shipments = lazy(() => import("./page/Livraison/Shipments.jsx"));
const AllOrdersPage = lazy(() => import("./page/orders/pages/orders/AllOrdersPage.jsx"));
const UploadData = lazy(() => import("./page/orders/pages/orders/UploadData.jsx"));

const AdminMarketplaceDashboard = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceDashboard.jsx"));
const AdminMarketplaceProfiles = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceProfiles.jsx"));
const AdminMarketplaceProfileDetail = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceProfileDetail.jsx"));
const AdminMarketplacePerformance = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplacePerformance.jsx"));
const AdminMarketplaceCollaborations = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceCollaborations.jsx"));
const AdminMarketplaceWallet = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceWallet.jsx"));
const AdminMarketplaceCommissions = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceCommissions.jsx"));
const AdminMarketplaceInvoices = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceInvoices.jsx"));
const AdminMarketplaceMessaging = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceMessaging.jsx"));
const AdminMarketplaceNotifications = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceNotifications.jsx"));
const AdminMarketplaceAuditLogs = lazy(() => import("./page/admin-marketplace/pages/AdminMarketplaceAuditLogs.jsx"));
const SettingsPage = lazy(() => import("./page/settings/SettingsPage.jsx"));


// Pages chargées à la demande (un fichier par page) : premier affichage plus rapide.
function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" aria-hidden="true" />
      <span className="sr-only">Chargement de la page…</span>
    </div>
  );
}

/* ================================================================
   PERMISSION GUARD — redirects if child user lacks the permission
================================================================ */
function PermGuard({ permission, userPermissions, children }) {
  if (!permission || !userPermissions) return children;
  if (userPermissions.includes("*")) return children;
  if (userPermissions.includes(permission)) return children;
  // redirect to settings (always accessible) to avoid redirect loops
  return <Navigate to="/settings" replace />;
}

const AppCommercant = () => {
  // Menu réduit : choix mémorisé ; par défaut réduit sur les écrans moyens
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("teqa.sidebar.collapsed");
      if (saved !== null) return saved === "1";
    } catch {
      /* stockage indisponible */
    }
    return typeof window !== "undefined" && window.innerWidth < 1280;
  });
  useEffect(() => {
    try {
      localStorage.setItem("teqa.sidebar.collapsed", collapsed ? "1" : "0");
    } catch {
      /* stockage indisponible */
    }
  }, [collapsed]);

  // user/permissions déjà chargés et normalisés par AuthContext (voir
  // context/AuthContext.jsx::normalizePermissions) — PrivateRoute garantit
  // que ce composant n'est monté qu'une fois loading=false et user non nul,
  // plus besoin d'un second appel getCurrentUser() ici (c'était une source
  // d'appel réseau dupliqué + de warning ESLint sur l'effet async).
  const { user } = useContext(AuthContext);

  if (!user) return null;

  if (user.status === "INACTIVE") {
    return (<MaintenancePage />);
  }

  const perms = Array.isArray(user.permissions) ? user.permissions : [];

  return (
  <BrowserRouter>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">

      {/* Sidebar */}
      <Saidbar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        userPermissions={perms}
      />

      {/* Main content */}
      <div
        className={`min-w-0 pt-14 transition-[padding] duration-300 ease-out sm:pt-0 ${
          collapsed ? "sm:pl-[72px]" : "sm:pl-[320px]"
        }`}
      >
        <main className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-5 md:px-6 md:py-6">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/management/orders/dashboard" element={<PermGuard permission="orders.view" userPermissions={perms}><OrdersDashboard/></PermGuard>}/>
                    <Route path="/management/orders/pending" element={<PermGuard permission="orders.view" userPermissions={perms}><PendingOrders/></PermGuard>}/>
                    <Route path="/management/orders/delivered" element={<PermGuard permission="orders.view" userPermissions={perms}><DeliveryDispatch/></PermGuard>}/>
                    <Route path="/management/orders" element={<PermGuard permission="orders.view" userPermissions={perms}><AllOrdersPage/></PermGuard>}/>
                    <Route path="/management/tracking" element={<PermGuard permission="orders.view" userPermissions={perms}><OrderTracking/></PermGuard>}/>
                    <Route path="/management/orders/add" element={<PermGuard permission="orders.assign" userPermissions={perms}><AddOrders/></PermGuard>}/>
                    <Route path="/management/orders/add_data" element={<PermGuard permission="orders.assign" userPermissions={perms}><UploadData/></PermGuard>}/>

                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/settings/delivery-partners" element={<Navigate to="/delivery/companies" replace />} />

                    <Route path="/integrations/add" element={<PermGuard permission="integrations.manage" userPermissions={perms}><IntegrationAdd/></PermGuard>}/>
                    <Route path="/integrations/list" element={<PermGuard permission="integrations.view" userPermissions={perms}><IntegrationList/></PermGuard>}/>
                    <Route path="/integrations/list/*" element={<PermGuard permission="integrations.view" userPermissions={perms}><IntegrationList/></PermGuard>}/>
                    <Route path="/integrations/products" element={<PermGuard permission="integrations.view" userPermissions={perms}><ProductsCatalog/></PermGuard>}/>
                    <Route path="/integration" element={<PermGuard permission="integrations.view" userPermissions={perms}><IntegrationDashboard/></PermGuard>}/>
                    <Route path="/integration/State" element={<PermGuard permission="integrations.view" userPermissions={perms}><IntegrationState/></PermGuard>}/>

                    <Route path="/delivery/add-company" element={<PermGuard permission="*" userPermissions={perms}><LivraisonAdd/></PermGuard>}/>
                    <Route path="/delivery/companies" element={<PermGuard permission="*" userPermissions={perms}><LivraisonList/></PermGuard>}/>
                    <Route path="/delivery/shipments" element={<PermGuard permission="*" userPermissions={perms}><Shipments /></PermGuard>}/>
                    <Route path="/delivery/connect/:slug" element={<PermGuard permission="*" userPermissions={perms}><DeliveryConnect /></PermGuard>} />

                    <Route path="/subscriptions/dashboard" element={<SubscriptionDashboard/>}/>
                    <Route path="/subscriptions/list" element={<SubscriptionList/>}/>

                      <Route path="/users/list" element={<PermGuard permission="team.view" userPermissions={perms}><TeamUsersList /></PermGuard>} />
                      <Route path="/users/add" element={<PermGuard permission="team.create" userPermissions={perms}><TeamAddUser /></PermGuard>} />

                                <Route path="/clients/dashboard" element={<ClientsDashboard />} />
                                <Route path="/clients/list" element={<ClientsList/>} />
                                <Route path="/clients/Add" element={<ClientAdd/>} />
                                <Route path="/clients/scoring" element={<ClientScoring />} />

                      <Route path="/admin-marketplace/dashboard" element={<PermGuard permission="marketplace.view" userPermissions={perms}><AdminMarketplaceDashboard /></PermGuard>} />
                      <Route path="/admin-marketplace/profiles" element={<PermGuard permission="marketplace.view" userPermissions={perms}><AdminMarketplaceProfiles /></PermGuard>} />
                      <Route path="/admin-marketplace/profiles/:type/:id" element={<PermGuard permission="marketplace.view" userPermissions={perms}><AdminMarketplaceProfileDetail /></PermGuard>} />
                      <Route path="/admin-marketplace/offers" element={<Navigate to="/admin-marketplace/profiles" replace />} />
                      <Route path="/admin-marketplace/performance" element={<PermGuard permission="collaborations.view" userPermissions={perms}><AdminMarketplacePerformance /></PermGuard>} />
                      <Route path="/admin-marketplace/collaborations" element={<PermGuard permission="collaborations.view" userPermissions={perms}><AdminMarketplaceCollaborations /></PermGuard>} />
                      <Route path="/admin-marketplace/wallet" element={<PermGuard permission="wallets.view_own" userPermissions={perms}><AdminMarketplaceWallet /></PermGuard>} />
                      <Route path="/admin-marketplace/commissions" element={<PermGuard permission="commissions.view_own" userPermissions={perms}><AdminMarketplaceCommissions /></PermGuard>} />
                      <Route path="/admin-marketplace/invoices" element={<PermGuard permission="invoices.view_own" userPermissions={perms}><AdminMarketplaceInvoices /></PermGuard>} />
                      <Route path="/admin-marketplace/messaging" element={<PermGuard permission="messaging.view_own" userPermissions={perms}><AdminMarketplaceMessaging /></PermGuard>} />
                      <Route path="/admin-marketplace/notifications" element={<PermGuard permission="messaging.view_own" userPermissions={perms}><AdminMarketplaceNotifications /></PermGuard>} />
                      <Route path="/admin-marketplace/audit-logs" element={<PermGuard permission="*" userPermissions={perms}><AdminMarketplaceAuditLogs /></PermGuard>} />


                      <Route path="*" element={
                        !perms || perms.includes("*") || perms.includes("orders.view")
                          ? <OrdersDashboard/>
                          : <Navigate to="/settings" replace />
                      } />
                </Routes>
                </Suspense>
        </main>
      </div>
    </div>
   </BrowserRouter>

   );
};

export default AppCommercant;
